import React, { useState } from 'react';
import { History, X } from 'lucide-react';
import RecordHistoryDrawer from '../Audit/RecordHistoryDrawer';

const MAX_WIDTH = {
  sm: 'max-w-sm',
  md: 'max-w-md',
  lg: 'max-w-lg',
  xl: 'max-w-xl',
  '2xl': 'max-w-2xl',
  '3xl': 'max-w-3xl',
  '4xl': 'max-w-4xl',
  '6xl': 'max-w-6xl',
};

/**
 * Shell profissional para modais de formulário: cabeçalho fixo, corpo com scroll, rodapé com ações.
 */
const FormModalLayout = ({
  title,
  subtitle,
  onClose,
  children,
  footer,
  size = 'md',
  className = '',
  bodyClassName = '',
  /** z-index do overlay (ex.: z-[60] para modal empilhado sobre outro) */
  zIndexClass = 'z-50',
  /** Ícone Lucide opcional — mesmo padrão visual da Licença assistida por IA */
  headerIcon: HeaderIcon,
  /** Ações extras à esquerda do botão fechar (ex.: compartilhar) */
  headerExtra,
  /** Tipo do cadastro para abrir o histórico do registro */
  historyEntityType,
  /** Identificador do registro já persistido */
  historyEntityId,
}) => {
  const maxW = MAX_WIDTH[size] || MAX_WIDTH.md;
  const [showHistory, setShowHistory] = useState(false);
  const canShowHistory = Boolean(historyEntityType && historyEntityId);

  return (
    <div
      className={`fixed inset-0 ${zIndexClass} flex items-end justify-center bg-black/50 p-0 backdrop-blur-[1px] sm:items-center sm:p-4`}
      role="presentation"
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="form-modal-title"
        className={`flex max-h-[94dvh] w-full flex-col overflow-hidden rounded-t-2xl border border-gray-200 bg-white shadow-strong sm:max-h-[90vh] sm:rounded-xl ${maxW} ${className}`}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex shrink-0 items-start justify-between gap-3 border-b border-gray-100 bg-gradient-to-r from-primary-50/90 via-white to-white px-4 py-4 sm:gap-4 sm:px-6">
          <div className="flex min-w-0 flex-1 items-start gap-3">
            {HeaderIcon ? (
              <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-primary-100 text-primary-800 ring-1 ring-primary-200/50">
                <HeaderIcon className="h-5 w-5" aria-hidden />
              </div>
            ) : null}
            <div className="min-w-0 flex-1">
              <h2 id="form-modal-title" className="text-lg font-semibold tracking-tight text-gray-900">
                {title}
              </h2>
              {subtitle ? (
                <p className="mt-1 text-sm leading-relaxed text-gray-600">{subtitle}</p>
              ) : null}
            </div>
          </div>
          <div className="flex shrink-0 items-center gap-2">
            {canShowHistory ? (
              <button
                type="button"
                onClick={() => setShowHistory(true)}
                className="inline-flex items-center gap-1.5 rounded-lg px-2.5 py-1.5 text-sm font-medium text-primary-800 transition-colors hover:bg-white/80 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500"
                title="Ver histórico deste registro"
              >
                <History className="h-4 w-4" aria-hidden />
                Histórico
              </button>
            ) : null}
            {headerExtra ? <div className="flex items-center">{headerExtra}</div> : null}
            {onClose ? (
              <button
                type="button"
                onClick={onClose}
                className="shrink-0 rounded-lg p-2 text-gray-400 transition-colors hover:bg-white/80 hover:text-gray-600 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 focus-visible:ring-offset-2"
                aria-label="Fechar"
              >
                <X className="h-5 w-5" aria-hidden />
              </button>
            ) : null}
          </div>
        </div>

        <div className={`min-h-0 flex-1 overflow-y-auto px-4 py-4 sm:px-6 sm:py-5 ${bodyClassName}`}>{children}</div>

        {footer != null && footer !== false ? (
          <div className="flex shrink-0 flex-wrap items-center justify-end gap-2 border-t border-gray-100/90 bg-gray-50/95 px-4 py-3 backdrop-blur-sm sm:gap-3 sm:px-6 sm:py-4">
            {footer}
          </div>
        ) : null}
      </div>
      {showHistory && canShowHistory ? (
        <RecordHistoryDrawer
          entityType={historyEntityType}
          entityId={historyEntityId}
          title={typeof title === 'string' ? `Histórico · ${title}` : 'Histórico do registro'}
          onClose={() => setShowHistory(false)}
        />
      ) : null}
    </div>
  );
};

export default FormModalLayout;
